const { useState, useEffect, useRef } = React;
const useAuth = window.useAuth;

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accentHue": 262,
  "theme": "light",
  "density": "comfy",
  "headline": "Describe a website.\nWatch it build itself.",
  "subhead": "Forge turns plain language into a real, deployable site \u2014 in seconds, not sprints."
}/*EDITMODE-END*/;

function App() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [screen, setScreen] = useState("landing"); // "landing" | "dashboard" | "pricing" | "builder"
  const [currentProject, setCurrentProject] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("sign-in");
  const [settingsProject, setSettingsProject] = useState(null);
  // P347 — page-title localization. The screen-meta map below feeds
  // document.title; pre-fix titles were hardcoded English ("Pricing —
  // Forge", "Account — Forge") so a Russian/Uzbek user saw English
  // tab titles on every navigation. Subscribing here re-runs the
  // title effect when the user toggles language.
  const { t, lang } = window.useT();
  // P194 — prompt typed into the landing hero. Threaded into the
  // dashboard's NewProjectCard so the user's typed brief survives the
  // landing → dashboard hop instead of being dropped on the floor.
  const [seedPrompt, setSeedPrompt] = useState("");
  // Lazy-screens loaded flag. Tracks the global so that when screen
  // changes, we know whether we can mount the matching component or
  // need to wait for window.loadLazyScreens() to resolve first.
  const [screensReady, setScreensReady] = useState(!!window.__lazyScriptsLoaded);
  const auth = useAuth();
  const prevAuthedRef = useRef(false);

  // Kick off the lazy bundle the first time we're asked for any non-
  // landing screen, including direct hits to /pricing / /privacy etc.
  useEffect(() => {
    if (screen === "landing" || screensReady) return;
    let cancelled = false;
    window.loadLazyScreens()
      .then(() => { if (!cancelled) setScreensReady(true); })
      .catch(() => { if (!cancelled) window.toast?.("Couldn't load that screen — refresh and try again", { tone: "warn" }); });
    return () => { cancelled = true; };
  }, [screen, screensReady]);

  useEffect(() => {
    document.documentElement.style.setProperty("--accent-h", tweaks.accentHue);
    document.documentElement.dataset.theme = tweaks.theme;
    document.documentElement.dataset.density = tweaks.density;
    // P587 — Sync the browser-chrome theme-color meta with the
    // user's manual TweaksPanel pick. Pre-fix index.html shipped
    // two `prefers-color-scheme`-bound theme-color metas (P284),
    // which auto-switch on OS preference. But TweaksPanel's theme
    // toggle (`data-theme="light|dark"` override on <html>) didn't
    // touch the chrome — a user with OS=light + Tweaks=dark would
    // see a dark page with light browser chrome. Now we manage a
    // tagged `<meta data-theme-override>` that wins over the
    // media-bound metas (last meta with matching media wins, and
    // a non-media meta always matches). Removed when the user
    // resets Tweaks to "auto" (= no override; falls back to OS
    // pref). Same hex values as the index.html metas.
    const cool = tweaks.theme === "dark" ? "#1a1d22" : "#fbfbfd";
    const existing = document.querySelector('meta[data-theme-override="true"]');
    if (tweaks.theme === "light" || tweaks.theme === "dark") {
      if (existing) {
        existing.setAttribute("content", cool);
      } else {
        const tag = document.createElement("meta");
        tag.name = "theme-color";
        tag.content = cool;
        tag.dataset.themeOverride = "true";
        document.head.appendChild(tag);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [tweaks.accentHue, tweaks.theme, tweaks.density]);

  // Auto-redirect to dashboard on sign-in (any path). Operators land on
  // the operator queue instead.
  useEffect(() => {
    if (auth.isAuthed && !prevAuthedRef.current && screen === "landing") {
      setScreen(auth.role === "operator" ? "operator" : "dashboard");
    }
    if (!auth.isAuthed && prevAuthedRef.current) {
      setScreen("landing");
      setCurrentProject(null);
    }
    prevAuthedRef.current = auth.isAuthed;
  }, [auth.isAuthed, auth.role, screen]);

  // Payment-provider Checkout (Payme or Click) returns the browser to
  // /billing/success on completion. Catch the path on first paint, toast
  // the upgrade, refresh the session so we pick up plan='pro' from
  // /api/auth/me, then drop the user on the dashboard with a clean URL.
  useEffect(() => {
    if (location.pathname === "/billing/success") {
      // P217 — verify the auth state before claiming success. Pre-fix
      // the toast fired unconditionally; if the cookie was dead by the
      // time the user came back (gateway redirect, slow processing,
      // they followed an old emailed link), the user saw a false
      // success message → got bounced to dashboard → hit 401 →
      // __sessionExpiredMint reset state → landed on landing with a
      // confusing "session expired" toast on top.
      fetch("/api/auth/me", { credentials: "include" })
        .then((r) => r.json())
        .then((j) => {
          if (j.authed) {
            if (j.plan && window.refreshAuth) window.refreshAuth(j);
            // P397 — both branches localized via useT (t is in scope
            // from the App component above).
            window.toast(t("billing.successToast"), { tone: "success" });
            setScreen("dashboard");
          } else {
            // Cookie is dead. Push them to sign-in; they'll come back
            // to a refreshed plan check after they re-auth.
            window.toast(t("billing.confirmSignInToast"), { tone: "warn" });
            setAuthMode("sign-in");
            setAuthOpen(true);
          }
        })
        .catch(() => {});
      history.replaceState({}, "", "/");
    }
    {
      // P290 — was `location.search.includes("cancelled=1")`, which
      // false-positives on contrived strings like `?foo=cancelled=1`
      // (a substring match doesn't honor URL param boundaries). Use
      // URLSearchParams for the strict key=value lookup.
      const params = new URLSearchParams(location.search);
      if (location.pathname === "/pricing" && params.get("cancelled") === "1") {
        window.toast(t("billing.cancelledToast"), { tone: "warn" });
        history.replaceState({}, "", "/pricing");
      }
    }
    // SPA fallback covers /privacy + /terms routes; bridge them to the
    // app's screen state so the right component mounts on direct hit.
    // P186 — also detect paths the SPA doesn't recognise and route to
    // NotFound. Without this, hitting /banana served index.html with
    // screen="landing" → user saw the marketing page with /banana in
    // the URL bar. Confusing and bad SEO (a 404 indexed as landing).
    const KNOWN_PATHS = new Set([
      "/", "/pricing", "/templates", "/privacy", "/terms",
      "/dashboard", "/builder", "/operator", "/account",
      "/billing/success",
    ]);
    if (location.pathname === "/privacy") setScreen("privacy");
    else if (location.pathname === "/terms") setScreen("terms");
    else if (
      !KNOWN_PATHS.has(location.pathname)
      && !location.pathname.startsWith("/api/")
      && !location.pathname.startsWith("/dist/")
      && !location.pathname.startsWith("/vendor/")
      && !location.pathname.startsWith("/fonts/")
      && !location.pathname.startsWith("/.well-known/")
    ) {
      setScreen("notfound");
    }
    // Recovery from /api/auth/verify error pages: ?auth=open jumps
    // straight into the sign-in modal so a stale-link click is one
    // step from a fresh link.
    // P290 — strict URLSearchParams check; substring match would
    // fire on `?someauth=open`, `?auth=opened`, etc.
    {
      const params = new URLSearchParams(location.search);
      if (params.get("auth") === "open") {
        setAuthMode("sign-in");
        setAuthOpen(true);
        history.replaceState({}, "", location.pathname);
      }
    }
  }, []);

  // Per-screen canonical link. Lighthouse flags pages whose canonical
  // points elsewhere — since all SPA routes share index.html, the
  // baked-in <link rel="canonical" href="/"> would be wrong for any
  // non-landing screen. Update it as the user navigates.
  //
  // P169 — also update document.title per screen. The static title in
  // index.html ("Forge — Build websites by talking") was correct for
  // the marketing landing only; every other screen left the same tab
  // title across navigation, which is bad for browser-tab disambig
  // and for screen-reader page-change announcements.
  // P185 — also toggle a `<meta name="robots" content="noindex">` for
  // the authed SPA routes. robots.txt Disallows them but a crawler
  // that ignores robots.txt would otherwise index private content
  // because index.html ships without noindex. Defense in depth.
  useEffect(() => {
    const meta = {
      landing:   { path: "/",          title: t("title.landing"),   description: t("meta.descLanding"),   noindex: false },
      pricing:   { path: "/pricing",   title: t("title.pricing"),   description: t("meta.descPricing"),   noindex: false },
      templates: { path: "/templates", title: t("title.templates"), description: t("meta.descTemplates"), noindex: false },
      privacy:   { path: "/privacy",   title: t("title.privacy"),   description: null,                   noindex: false }, // legal.jsx adds its own
      terms:     { path: "/terms",     title: t("title.terms"),     description: null,                   noindex: false },
      dashboard: { path: null,         title: t("title.dashboard"), description: null,                   noindex: true },
      builder:   { path: null,         title: t("title.builder"),   description: null,                   noindex: true },
      operator:  { path: null,         title: t("title.operator"),  description: null,                   noindex: true },
      account:   { path: null,         title: t("title.account"),   description: null,                   noindex: true },
      notfound:  { path: null,         title: t("title.notfound"),  description: null,                   noindex: true },
    };
    const m = meta[screen];
    if (!m) return;
    // P641 — When on the builder, prefix the tab title with the
    // project name so multiple builder tabs are distinguishable in
    // the browser's tab strip and in screen-reader page-change
    // announcements. Pre-fix every builder tab read identically
    // ("Builder — Forge"), so a user editing 3 projects in 3 tabs
    // had to rely on the (very small) favicon + position to find
    // the right one. Falls back to the static title when the
    // project hasn't loaded yet (initial mount, transient state).
    if (m.title) {
      if (screen === "builder" && currentProject?.name) {
        document.title = `${currentProject.name} — ${m.title}`;
      } else {
        document.title = m.title;
      }
    }
    if (m.path) {
      const link = document.querySelector('link[rel="canonical"]');
      if (link) link.href = `https://mint-bux.pages.dev${m.path}`;
    }
    // P579 — Per-screen meta description for SEO. Pre-fix /pricing
    // and /templates inherited the static landing description from
    // index.html ("Forge turns a conversation into a real website
    // ..."); search engines indexed both with the same snippet.
    // Now landing/pricing/templates each get their own description.
    // Authed routes + legal pages skip the update (the static
    // landing description is fine when a crawler hits them, since
    // they're noindex'd anyway).
    if (m.description) {
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", m.description);
    }
    // Toggle noindex meta. Use a marker dataset so we don't fight with
    // legal.jsx's own noindex meta (which has data-legal-noindex).
    const existing = document.querySelector('meta[data-screen-noindex="true"]');
    if (m.noindex) {
      if (!existing) {
        const tag = document.createElement("meta");
        tag.name = "robots";
        tag.content = "noindex, follow";
        tag.dataset.screenNoindex = "true";
        document.head.appendChild(tag);
      }
    } else if (existing) {
      existing.remove();
    }
    // P641 — depend on currentProject?.name so the builder tab
    // title re-stamps when the user switches projects in-place
    // (rare path but possible via /api/auth/me race or starter-chip
    // → builder reset).
  }, [screen, lang, currentProject?.name]);

  // Scroll back to the top whenever the screen changes — otherwise the new
  // screen mounts with the previous screen's scroll offset, which feels
  // disorienting after long pages like Pricing or Templates.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [screen]);

  // Global navigation handle.
  useEffect(() => {
    window.openAuth = (mode = "sign-in") => {
      setAuthMode(mode);
      setAuthOpen(true);
    };
    window.goTo = (s, opts = {}) => {
      if (opts.project) setCurrentProject(opts.project);
      setScreen(s);
    };
    window.openProjectSettings = (project) => setSettingsProject(project);
    return () => {
      window.openAuth = () => {};
      window.goTo = () => {};
      window.openProjectSettings = () => {};
    };
  }, []);

  const openProject = (project) => {
    setCurrentProject(project);
    setScreen("builder");
    if (project?.id) window.touchProjectOpened?.(project.id);
  };

  // While the lazy bundle is loading for a non-landing screen, render
  // a quiet placeholder. Mostly invisible because the load is fast on
  // good connections, but covers the off-chance that the user lands
  // direct on /pricing on a slow line.
  if (screen !== "landing" && !screensReady) {
    return <ScreensLoading />;
  }

  return (
    <main id="main" tabIndex={-1}>
      {screen === "landing" && (
        <Landing
          tweaks={tweaks}
          onEnterBuilder={(prompt) => {
            // P194 — capture hero prompt + route. Authed users land on
            // the dashboard with the prompt pre-filled in the new-project
            // textarea; unauthed go to builder for the anonymous demo
            // path (which seeds its own prompt internally).
            if (typeof prompt === "string" && prompt.trim()) {
              setSeedPrompt(prompt.trim());
            }
            if (auth.isAuthed) setScreen("dashboard");
            else setScreen("builder");
          }}
        />
      )}
      {screen === "dashboard" && (
        <Dashboard
          onOpenProject={openProject}
          onBack={() => setScreen("landing")}
          seedPrompt={seedPrompt}
          onSeedPromptConsumed={() => setSeedPrompt("")}
        />
      )}
      {screen === "pricing" && (
        <Pricing onBack={() => setScreen("landing")} />
      )}
      {screen === "templates" && (
        <Templates onBack={() => setScreen("landing")} />
      )}
      {screen === "operator" && (
        <OperatorScreen
          onBack={() => setScreen("landing")}
          onOpenProject={(queueItem) => {
            const proj = window.getProject(queueItem.projectId) || {
              id: queueItem.projectId,
              name: queueItem.projectName,
              prompt: queueItem.notes,
              tag: "OPERATOR",
              swatch: "oklch(0.92 0.04 60)",
              ink: "oklch(0.25 0.05 30)",
              version: "v0.1",
              edits: 0,
            };
            setCurrentProject(proj);
            setScreen("builder");
          }}
        />
      )}
      {screen === "builder" && (
        <Builder
          project={currentProject}
          onBack={() => setScreen(
            auth.role === "operator" ? "operator" :
            auth.isAuthed ? "dashboard" : "landing"
          )}
        />
      )}
      {screen === "account" && (
        <Account onBack={() => setScreen(auth.isAuthed ? "dashboard" : "landing")} />
      )}
      {(screen === "privacy" || screen === "terms") && (
        <Legal kind={screen} onBack={() => setScreen("landing")} />
      )}
      {!["landing", "dashboard", "pricing", "templates", "operator", "builder", "account", "privacy", "terms"].includes(screen) && (
        <NotFound onHome={() => setScreen("landing")} />
      )}

      <AuthModal
        open={authOpen}
        initialMode={authMode}
        onClose={() => setAuthOpen(false)}
      />
      {typeof ProjectSettingsModal === "function" && (
        <ProjectSettingsModal
          open={!!settingsProject}
          project={settingsProject}
          onClose={() => setSettingsProject(null)}
        />
      )}
      <CommandPalette />
      <Toaster />

      <TweaksPanel title="Tweaks">
        <TweakSection title="Theme">
          <TweakRadio
            label="Mode"
            value={tweaks.theme}
            options={[
              { value: "light", label: "Light" },
              { value: "dark", label: "Dark" },
            ]}
            onChange={(v) => setTweak("theme", v)}
          />
          <TweakSlider
            label="Accent hue"
            value={tweaks.accentHue}
            min={0}
            max={360}
            step={1}
            onChange={(v) => setTweak("accentHue", v)}
          />
          <TweakRadio
            label="Density"
            value={tweaks.density}
            options={[
              { value: "compact", label: "Compact" },
              { value: "comfy", label: "Comfy" },
              { value: "loose", label: "Loose" },
            ]}
            onChange={(v) => setTweak("density", v)}
          />
        </TweakSection>
        <TweakSection title="Hero copy">
          <TweakText
            label="Headline"
            value={tweaks.headline}
            onChange={(v) => setTweak("headline", v)}
            multiline
          />
          <TweakText
            label="Subhead"
            value={tweaks.subhead}
            onChange={(v) => setTweak("subhead", v)}
            multiline
          />
        </TweakSection>
        <TweakSection title="Navigation">
          <TweakRadio
            label="Screen"
            value={screen}
            options={[
              { value: "landing", label: "Land" },
              { value: "dashboard", label: "Dash" },
              { value: "pricing", label: "Price" },
              { value: "templates", label: "Tpls" },
              { value: "operator", label: "Ops" },
              { value: "builder", label: "Build" },
            ]}
            onChange={(v) => setScreen(v)}
          />
          <TweakButton
            label="Trigger 404 (demo)"
            secondary
            onClick={() => setScreen("404-demo")}
          />
          <TweakButton
            label="Open ⌘K palette"
            secondary
            onClick={() => window.openPalette?.()}
          />
        </TweakSection>
        <TweakSection title="Language">
          <TweakRadio
            label="Locale"
            value={window.getLang?.() ?? "en"}
            options={[
              { value: "en", label: "EN" },
              { value: "ru", label: "RU" },
              { value: "uz", label: "UZ" },
            ]}
            onChange={(v) => window.setLang?.(v)}
          />
        </TweakSection>
        <TweakSection title="Demo: auth">
          <TweakButton
            label={auth.isAuthed ? `Sign out (${auth.name})` : "Open sign-in modal"}
            onClick={() => auth.isAuthed ? window.signOut() : window.openAuth("sign-in")}
          />
          {!auth.isAuthed && (
            <>
              <TweakButton
                label="Open sign-up modal"
                secondary
                onClick={() => window.openAuth("sign-up")}
              />
              <TweakButton
                label="Sign in as operator"
                secondary
                onClick={() => window.signInAsOperator()}
              />
            </>
          )}
          {auth.isAuthed && auth.role !== "operator" && (
            <TweakButton
              label="Switch to operator"
              secondary
              onClick={() => { window.signOut(); setTimeout(() => window.signInAsOperator(), 50); }}
            />
          )}
        </TweakSection>
      </TweaksPanel>
    </main>
  );
}

function NotFound({ onHome }) {
  // P418 — surface localized.
  const { t } = window.useT();
  return (
    <div style={notFoundStyles.page} data-screen-label="404">

      <div style={notFoundStyles.kicker}>{t("nf.kicker")}</div>
      <h1 style={notFoundStyles.h1}>{t("nf.h1")}</h1>
      <p style={notFoundStyles.body}>{t("nf.body")}</p>
      <div style={notFoundStyles.actions}>
        {/* P558 — explicit type="button" continues the P501-P516
            defensive sweep. NotFound has no <form> wrapper, so the
            HTML default of type="submit" no-ops in practice — but
            consistency matters and a future <form> wrap would
            silently break with implicit submit. */}
        <button type="button" style={notFoundStyles.btnSolid} onClick={onHome}><span aria-hidden="true">← </span>{t("nf.btnHome")}</button>
        <button type="button" style={notFoundStyles.btnGhost} onClick={() => window.toast(t("nf.bugReportToast"))}>
          {t("nf.btnReport")}
        </button>
      </div>
      <div style={notFoundStyles.bigDigit} data-drift-bg aria-hidden="true">404</div>
    </div>
  );
}

const notFoundStyles = {
  page: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    background: "var(--paper)",
    color: "var(--ink)",
    fontFamily: "var(--font-ui)",
    textAlign: "center",
    overflow: "hidden",
  },
  kicker: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    letterSpacing: "0.12em",
    color: "var(--accent)",
    marginBottom: 18,
    zIndex: 1,
  },
  h1: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: "clamp(48px, 8vw, 96px)",
    lineHeight: 1.05,
    letterSpacing: "-0.025em",
    maxWidth: 800,
    zIndex: 1,
  },
  em: { fontStyle: "italic", color: "var(--accent)" },
  body: {
    color: "var(--ink-2)",
    fontSize: 16,
    lineHeight: 1.5,
    maxWidth: 460,
    marginTop: 18,
    textWrap: "pretty",
    zIndex: 1,
  },
  actions: { display: "flex", gap: 8, marginTop: 28, zIndex: 1 },
  btnSolid: {
    padding: "10px 20px",
    background: "var(--ink)",
    color: "var(--paper)",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 500,
  },
  btnGhost: {
    padding: "10px 20px",
    background: "transparent",
    border: "1px solid var(--rule)",
    color: "var(--ink-2)",
    borderRadius: 999,
    fontSize: 14,
  },
  bigDigit: {
    position: "absolute",
    fontFamily: "var(--font-display)",
    fontStyle: "italic",
    fontSize: "min(88vw, 1280px)",
    lineHeight: 1,
    letterSpacing: "-0.05em",
    color: "color-mix(in oklab, var(--ink) 4%, transparent)",
    pointerEvents: "none",
    zIndex: 0,
    userSelect: "none",
  },
};

// ── Error reporting ──────────────────────────────────────────────────
// Two channels: React render errors via the boundary below, and any
// uncaught script error / unhandled promise rejection via window
// listeners. Both POST a JSON report to /api/log/client where it lands
// in the same structured-log feed as server errors.
function _reportClientError(payload) {
  try {
    fetch("/api/log/client", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...payload, url: location.href, ua: navigator.userAgent, at: Date.now() }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never throw from the reporter itself */ }
}
window.addEventListener("error", (ev) => {
  _reportClientError({
    kind: "uncaught-error",
    message: ev?.message || String(ev?.error || ""),
    stack: (ev?.error?.stack || "").slice(0, 2000),
  });
});
window.addEventListener("unhandledrejection", (ev) => {
  const reason = ev?.reason;
  _reportClientError({
    kind: "unhandled-rejection",
    message: reason?.message || String(reason || ""),
    stack: (reason?.stack || "").slice(0, 2000),
  });
});

function ScreensLoading() {
  // Same paper-and-ink aesthetic as the rest of the app, no spinner —
  // a calm typographic placeholder fades in if the load takes >150ms.
  // Below that threshold, screens arrive before this even renders.
  // P400 — copy localized via useT.
  const { t } = window.useT();
  const wrap = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
    background: "var(--paper)",
    color: "var(--ink-2)",
    textAlign: "center",
  };
  const note = {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    letterSpacing: "0.1em",
    color: "var(--ink-3)",
    textTransform: "uppercase",
    animation: "stagger-in 360ms 150ms cubic-bezier(.2,.7,.4,1) both",
  };
  return (
    <div style={wrap} role="status" aria-live="polite">
      <div style={note}>{t("loading.screens")}</div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, key: 0 };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    _reportClientError({
      kind: "render-error",
      message: error?.message || String(error),
      stack: (error?.stack || "").slice(0, 2000),
      componentStack: (info?.componentStack || "").slice(0, 2000),
    });
  }
  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          onRetry={() => this.setState({ hasError: false, key: this.state.key + 1 })}
        />
      );
    }
    return <React.Fragment key={this.state.key}>{this.props.children}</React.Fragment>;
  }
}

function ErrorFallback({ onRetry }) {
  // P294 — pre-fix used a conditional hook call:
  //   const t = window.useT ? window.useT().t : (k) => k;
  // useT is a real hook (useState + useEffect), so the count
  // differed across renders if window.useT availability changed
  // (catastrophic-shared-load → recovery race) → React's
  // hook-order tracker fires "Rendered more hooks than during the
  // previous render" inside the error fallback ITSELF, masking
  // the original error. Split into two leaf components with
  // unconditional hooks each; the parent picks one based on
  // window.useT presence (a non-hook decision).
  if (!window.useT) return <ErrorFallbackBare onRetry={onRetry} />;
  return <ErrorFallbackTranslated onRetry={onRetry} />;
}

function ErrorFallbackTranslated({ onRetry }) {
  const { t } = window.useT();
  return <ErrorFallbackInner onRetry={onRetry} t={t} />;
}

function ErrorFallbackBare({ onRetry }) {
  // P298 — Inline English copy synchronized with shared.jsx's
  // canonical `err.*` strings (P294's first cut had its own
  // slightly-different wording, which would have drifted as the
  // i18n strings evolved). Last-resort copy if shared.js itself
  // failed to load and the i18n bundle is unreachable.
  const en = {
    "err.title": "Something went sideways.",
    "err.body": "Your work is safe. The error has been reported and we'll look at it. Try again, or head back to the dashboard.",
    "err.tryAgain": "Try again",
    "err.backHome": "Back home",
  };
  return <ErrorFallbackInner onRetry={onRetry} t={(k) => en[k] || k} />;
}

function ErrorFallbackInner({ onRetry, t }) {
  const wrap = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
    background: "var(--paper)",
    color: "var(--ink)",
    textAlign: "center",
    gap: 18,
  };
  const mark = {
    width: 56, height: 56, borderRadius: 12,
    background: "var(--ink)", color: "var(--paper)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 24,
  };
  const title = {
    fontFamily: "var(--font-display)",
    fontStyle: "italic",
    fontSize: 32,
    lineHeight: 1.1,
    letterSpacing: "-0.01em",
    maxWidth: 480,
  };
  const body = {
    color: "var(--ink-2)",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: 440,
  };
  const btnRow = { display: "flex", gap: 10, marginTop: 6 };
  const primary = {
    padding: "10px 18px",
    background: "var(--ink)",
    color: "var(--paper)",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 500,
  };
  const secondary = {
    padding: "10px 16px",
    background: "transparent",
    border: "1px solid var(--rule)",
    color: "var(--ink-2)",
    borderRadius: 999,
    fontSize: 13,
  };
  return (
    <div style={wrap} data-screen-label="error-fallback" role="alert">
      <div style={mark} aria-hidden="true">◆</div>
      <h1 style={title}>{t("err.title")}</h1>
      <p style={body}>{t("err.body")}</p>
      <div style={btnRow}>
        {/* P558 — same defensive type="button" continuation. */}
        <button type="button" style={primary} onClick={onRetry}>{t("err.tryAgain")}</button>
        <button type="button" style={secondary} onClick={() => { location.href = "/"; }}>{t("err.backHome")}</button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
